/**
 * QuizEngine — Motor de evaluación interactiva
 * Oculta diagnóstico, permite explorar, evalúa al alumno
 */

import { QuizState, QuizAnswer, QuizQuestion } from './types';
import { QUIZ_CASES } from './quiz-data';
import { Patient } from '../medical/Patient';
import state from '../core/state';

export class QuizEngine {
  private container: HTMLElement | null = null;
  private quizState: QuizState | null = null;
  private patient: Patient | null = null;
  private explorationTimer: number = 0;
  private onComplete?: (score: number, maxScore: number) => void;

  constructor() {
    this.injectStyles();
  }

  start(caseId: string, patient: Patient, onComplete?: (score: number, maxScore: number) => void): void {
    const config = QUIZ_CASES[caseId];
    if (!config) return;

    this.patient = patient;
    this.onComplete = onComplete;

    // Ocultar nodos de diagnóstico
    const hiddenIds = patient.hideNodesByType('diagnosis');

    this.quizState = {
      caseId,
      questions: [...config.questions],
      currentIndex: 0,
      answers: [],
      startTime: Date.now(),
      score: 0,
      maxScore: config.questions.length * 100,
      isComplete: false,
      isExploring: true,
      hiddenNodeIds: hiddenIds,
    };

    state.setEducationMode('quiz');
    state.emit({ type: 'QUIZ_START', caseId });

    // Mostrar fase de exploración
    this.showExploration(config.explorationTimeSeconds);
  }

  stop(): void {
    this.container?.remove();
    this.container = null;
    this.quizState = null;
    this.patient = null;
    state.setEducationMode('none');
    clearInterval(this.explorationTimer);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // FASE 1: EXPLORACIÓN
  // ═══════════════════════════════════════════════════════════════════════

  private showExploration(seconds: number): void {
    this.ensureContainer();
    let remaining = seconds;

    this.container!.innerHTML = `
      <div class="quiz-explore">
        <div class="quiz-explore-icon">&#x1F50D;</div>
        <h2>Explora el caso clínico</h2>
        <p>Examina los signos vitales, síntomas y laboratorios.<br>El diagnóstico está oculto.</p>
        <div class="quiz-explore-timer">
          <div class="quiz-explore-bar">
            <div class="quiz-explore-fill" id="explore-fill"></div>
          </div>
          <span id="explore-time">${remaining}s</span>
        </div>
        <button class="quiz-btn quiz-btn-primary" id="quiz-ready-btn">Estoy listo para responder</button>
      </div>
    `;

    const fill = this.container!.querySelector('#explore-fill') as HTMLElement;
    const timeLabel = this.container!.querySelector('#explore-time') as HTMLElement;
    const readyBtn = this.container!.querySelector('#quiz-ready-btn') as HTMLElement;

    readyBtn.addEventListener('click', () => {
      clearInterval(this.explorationTimer);
      this.startQuestions();
    });

    this.explorationTimer = window.setInterval(() => {
      remaining--;
      if (remaining <= 0) {
        clearInterval(this.explorationTimer);
        this.startQuestions();
        return;
      }
      timeLabel.textContent = `${remaining}s`;
      fill.style.width = `${((seconds - remaining) / seconds) * 100}%`;
    }, 1000);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // FASE 2: PREGUNTAS
  // ═══════════════════════════════════════════════════════════════════════

  private startQuestions(): void {
    if (!this.quizState) return;
    this.quizState.isExploring = false;
    this.renderQuestion();
  }

  private renderQuestion(): void {
    if (!this.quizState || !this.container) return;

    const q = this.quizState.questions[this.quizState.currentIndex];
    const total = this.quizState.questions.length;
    const current = this.quizState.currentIndex + 1;

    // Progress dots
    const dots = this.quizState.questions.map((_, i) => {
      const cls = i < this.quizState!.currentIndex ? 'done' : i === this.quizState!.currentIndex ? 'active' : '';
      return `<span class="quiz-dot ${cls}"></span>`;
    }).join('');

    const typeLabels: Record<string, string> = {
      diagnosis: 'Diagnóstico',
      triage: 'Triage',
      pattern: 'Patrón clínico',
      next_step: 'Conducta',
    };

    this.container.innerHTML = `
      <div class="quiz-overlay" id="quiz-overlay">
        <div class="quiz-card">
          <div class="quiz-progress">
            <div class="quiz-dots">${dots}</div>
            <span class="quiz-counter">Pregunta ${current} de ${total}</span>
          </div>
          <div class="quiz-type-badge">${typeLabels[q.type] || q.type}</div>
          <h2 class="quiz-question">${q.text}</h2>
          <div class="quiz-options" id="quiz-options">
            ${q.options.map((opt, i) => `
              <button class="quiz-option" data-index="${i}">
                <span class="quiz-option-letter">${String.fromCharCode(65 + i)}</span>
                <span class="quiz-option-text">${opt.label}</span>
              </button>
            `).join('')}
          </div>
        </div>
      </div>
    `;

    // Event delegation
    const optionsContainer = this.container.querySelector('#quiz-options')!;
    optionsContainer.addEventListener('click', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('.quiz-option') as HTMLElement;
      if (!btn || btn.classList.contains('disabled')) return;
      const index = parseInt(btn.dataset.index!);
      this.handleAnswer(index);
    });
  }

  private handleAnswer(selectedIndex: number): void {
    if (!this.quizState || !this.container) return;

    const q = this.quizState.questions[this.quizState.currentIndex];
    const isCorrect = selectedIndex === q.correctIndex;
    const timeElapsed = Date.now() - this.quizState.startTime;

    // Calcular score (100 base, bonus por velocidad)
    const points = isCorrect ? 100 : 0;
    this.quizState.score += points;

    const answer: QuizAnswer = {
      questionId: q.id,
      selectedIndex,
      isCorrect,
      timeElapsed,
    };
    this.quizState.answers.push(answer);

    state.emit({ type: 'QUIZ_ANSWER', questionId: q.id, correct: isCorrect });

    // Mostrar feedback
    this.showFeedback(q, selectedIndex, isCorrect);

    // Si es la pregunta de diagnóstico y acertó, revelar nodo
    if (isCorrect && q.type === 'diagnosis' && this.patient) {
      this.quizState.hiddenNodeIds.forEach(id => {
        this.patient!.revealNode(id);
      });
    }
  }

  private showFeedback(q: QuizQuestion, selected: number, isCorrect: boolean): void {
    if (!this.container) return;

    const options = this.container.querySelectorAll('.quiz-option');
    options.forEach((opt, i) => {
      const el = opt as HTMLElement;
      el.classList.add('disabled');
      if (i === q.correctIndex) el.classList.add('correct');
      if (i === selected && !isCorrect) el.classList.add('wrong');
    });

    // Insertar feedback
    const card = this.container.querySelector('.quiz-card')!;
    const feedback = document.createElement('div');
    feedback.className = `quiz-feedback ${isCorrect ? 'quiz-feedback-correct' : 'quiz-feedback-wrong'}`;
    feedback.innerHTML = `
      <div class="quiz-feedback-header">
        <span class="quiz-feedback-icon">${isCorrect ? '&#x2705;' : '&#x274C;'}</span>
        <span>${isCorrect ? 'Correcto' : 'Incorrecto'}</span>
      </div>
      <p class="quiz-feedback-text">${q.explanation}</p>
      <button class="quiz-btn quiz-btn-primary" id="quiz-next-btn">
        ${this.quizState!.currentIndex < this.quizState!.questions.length - 1 ? 'Siguiente' : 'Ver resultados'}
      </button>
    `;
    card.appendChild(feedback);

    this.container.querySelector('#quiz-next-btn')!.addEventListener('click', () => {
      this.quizState!.currentIndex++;
      if (this.quizState!.currentIndex >= this.quizState!.questions.length) {
        this.showResults();
      } else {
        this.renderQuestion();
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // FASE 3: RESULTADOS
  // ═══════════════════════════════════════════════════════════════════════

  private showResults(): void {
    if (!this.quizState || !this.container) return;
    this.quizState.isComplete = true;

    const score = this.quizState.score;
    const max = this.quizState.maxScore;
    const pct = Math.round((score / max) * 100);
    const correct = this.quizState.answers.filter(a => a.isCorrect).length;
    const total = this.quizState.questions.length;

    let grade: string, gradeColor: string;
    if (pct >= 90) { grade = 'Excelente'; gradeColor = '#30D158'; }
    else if (pct >= 70) { grade = 'Bien'; gradeColor = '#2997FF'; }
    else if (pct >= 50) { grade = 'Regular'; gradeColor = '#FF9F0A'; }
    else { grade = 'Necesita repaso'; gradeColor = '#FF453A'; }

    const elapsed = Math.round((Date.now() - this.quizState.startTime) / 1000);
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;

    state.emit({ type: 'QUIZ_COMPLETE', score, maxScore: max });

    // Revelar diagnósticos si no se revelaron antes
    if (this.patient) {
      this.quizState.hiddenNodeIds.forEach(id => {
        const node = this.patient!.getNode(id);
        if (node && !node.visible) this.patient!.revealNode(id);
      });
    }

    this.container.innerHTML = `
      <div class="quiz-overlay">
        <div class="quiz-card quiz-results">
          <div class="quiz-results-header">
            <div class="quiz-results-score" style="color: ${gradeColor}">${pct}%</div>
            <div class="quiz-results-grade" style="color: ${gradeColor}">${grade}</div>
          </div>

          <div class="quiz-results-stats">
            <div class="quiz-stat">
              <span class="quiz-stat-value">${correct}/${total}</span>
              <span class="quiz-stat-label">Correctas</span>
            </div>
            <div class="quiz-stat">
              <span class="quiz-stat-value">${mins}:${secs.toString().padStart(2, '0')}</span>
              <span class="quiz-stat-label">Tiempo</span>
            </div>
            <div class="quiz-stat">
              <span class="quiz-stat-value">${score}</span>
              <span class="quiz-stat-label">Puntos</span>
            </div>
          </div>

          <div class="quiz-results-review">
            ${this.quizState.answers.map((a, i) => {
              const q = this.quizState!.questions[i];
              return `
                <div class="quiz-review-item ${a.isCorrect ? 'correct' : 'wrong'}">
                  <span class="quiz-review-icon">${a.isCorrect ? '&#x2705;' : '&#x274C;'}</span>
                  <span class="quiz-review-text">${q.text.substring(0, 60)}...</span>
                </div>
              `;
            }).join('')}
          </div>

          <button class="quiz-btn quiz-btn-primary" id="quiz-close-btn">Volver al caso</button>
        </div>
      </div>
    `;

    this.container.querySelector('#quiz-close-btn')!.addEventListener('click', () => {
      this.onComplete?.(score, max);
      this.stop();
    });
  }

  // ═══════════════════════════════════════════════════════════════════════
  // DOM
  // ═══════════════════════════════════════════════════════════════════════

  private ensureContainer(): void {
    if (this.container) return;
    this.container = document.createElement('div');
    this.container.id = 'quiz-container';
    document.body.appendChild(this.container);
  }

  private injectStyles(): void {
    if (document.getElementById('quiz-styles')) return;
    const style = document.createElement('style');
    style.id = 'quiz-styles';
    style.textContent = `
      #quiz-container {
        position: fixed; inset: 0; z-index: 5000;
        display: flex; align-items: center; justify-content: center;
        pointer-events: none;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      }

      .quiz-explore {
        pointer-events: auto;
        background: rgba(0,0,0,0.85);
        backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
        border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 20px;
        padding: 40px 48px;
        text-align: center;
        max-width: 440px;
        animation: quizIn 0.4s cubic-bezier(0.25,1,0.5,1);
      }
      .quiz-explore-icon { font-size: 40px; margin-bottom: 16px; }
      .quiz-explore h2 {
        font-size: 22px; font-weight: 700; color: #f5f5f7;
        letter-spacing: -0.5px; margin-bottom: 8px;
      }
      .quiz-explore p {
        font-size: 14px; color: #86868b; line-height: 1.5; margin-bottom: 24px;
      }
      .quiz-explore-timer {
        display: flex; align-items: center; gap: 12px; margin-bottom: 24px;
      }
      .quiz-explore-bar {
        flex: 1; height: 4px; background: rgba(255,255,255,0.08);
        border-radius: 2px; overflow: hidden;
      }
      .quiz-explore-fill {
        height: 100%; width: 0; background: #2997FF;
        border-radius: 2px; transition: width 1s linear;
      }
      .quiz-explore-timer span {
        font-family: 'JetBrains Mono', monospace;
        font-size: 13px; color: #86868b; min-width: 32px;
      }

      .quiz-overlay {
        pointer-events: auto;
        position: fixed; inset: 0;
        background: rgba(0,0,0,0.6);
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        display: flex; align-items: center; justify-content: center;
        animation: quizFade 0.3s ease;
      }
      .quiz-card {
        background: #111; border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 20px; padding: 32px;
        max-width: 520px; width: 90%;
        animation: quizIn 0.4s cubic-bezier(0.25,1,0.5,1);
      }

      .quiz-progress {
        display: flex; align-items: center; justify-content: space-between;
        margin-bottom: 20px;
      }
      .quiz-dots { display: flex; gap: 6px; }
      .quiz-dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: rgba(255,255,255,0.1);
      }
      .quiz-dot.active { background: #2997FF; }
      .quiz-dot.done { background: #30D158; }
      .quiz-counter { font-size: 12px; color: #6e6e73; }

      .quiz-type-badge {
        display: inline-block;
        font-size: 11px; font-weight: 600; text-transform: uppercase;
        letter-spacing: 1px; color: #2997FF;
        margin-bottom: 12px;
      }
      .quiz-question {
        font-size: 20px; font-weight: 700; color: #f5f5f7;
        letter-spacing: -0.3px; line-height: 1.3; margin-bottom: 24px;
      }

      .quiz-options { display: flex; flex-direction: column; gap: 8px; }
      .quiz-option {
        display: flex; align-items: center; gap: 14px;
        padding: 14px 18px; border-radius: 12px;
        background: rgba(255,255,255,0.04);
        border: 1px solid rgba(255,255,255,0.06);
        cursor: pointer; transition: all 0.2s;
        color: #f5f5f7; font-size: 14px; text-align: left;
        font-family: inherit;
      }
      .quiz-option:hover:not(.disabled) {
        background: rgba(255,255,255,0.08);
        border-color: rgba(41,151,255,0.3);
        transform: translateX(4px);
      }
      .quiz-option.disabled { pointer-events: none; opacity: 0.6; }
      .quiz-option.correct {
        background: rgba(48,209,88,0.1) !important;
        border-color: rgba(48,209,88,0.4) !important;
        opacity: 1 !important;
      }
      .quiz-option.wrong {
        background: rgba(255,69,58,0.1) !important;
        border-color: rgba(255,69,58,0.4) !important;
        opacity: 1 !important;
      }
      .quiz-option-letter {
        width: 28px; height: 28px; border-radius: 8px;
        background: rgba(255,255,255,0.06);
        display: flex; align-items: center; justify-content: center;
        font-size: 12px; font-weight: 600; color: #86868b;
        flex-shrink: 0;
      }

      .quiz-feedback {
        margin-top: 20px; padding: 16px 20px; border-radius: 12px;
        animation: quizIn 0.3s ease;
      }
      .quiz-feedback-correct {
        background: rgba(48,209,88,0.08);
        border: 0.5px solid rgba(48,209,88,0.2);
      }
      .quiz-feedback-wrong {
        background: rgba(255,69,58,0.08);
        border: 0.5px solid rgba(255,69,58,0.2);
      }
      .quiz-feedback-header {
        display: flex; align-items: center; gap: 8px;
        font-size: 15px; font-weight: 600; color: #f5f5f7;
        margin-bottom: 8px;
      }
      .quiz-feedback-text {
        font-size: 13px; color: #a1a1a6; line-height: 1.5;
        margin-bottom: 16px;
      }

      .quiz-results { text-align: center; }
      .quiz-results-header { margin-bottom: 28px; }
      .quiz-results-score {
        font-size: 64px; font-weight: 800; letter-spacing: -3px;
        line-height: 1;
      }
      .quiz-results-grade {
        font-size: 18px; font-weight: 600; margin-top: 4px;
      }
      .quiz-results-stats {
        display: flex; justify-content: center; gap: 32px;
        margin-bottom: 28px;
      }
      .quiz-stat {
        display: flex; flex-direction: column; align-items: center; gap: 4px;
      }
      .quiz-stat-value {
        font-family: 'JetBrains Mono', monospace;
        font-size: 20px; font-weight: 700; color: #f5f5f7;
      }
      .quiz-stat-label { font-size: 11px; color: #6e6e73; text-transform: uppercase; letter-spacing: 0.5px; }

      .quiz-results-review {
        display: flex; flex-direction: column; gap: 6px;
        margin-bottom: 24px; text-align: left;
      }
      .quiz-review-item {
        display: flex; align-items: center; gap: 10px;
        padding: 10px 14px; border-radius: 10px;
        background: rgba(255,255,255,0.03);
        font-size: 13px; color: #a1a1a6;
      }
      .quiz-review-item.correct { border-left: 3px solid #30D158; }
      .quiz-review-item.wrong { border-left: 3px solid #FF453A; }

      .quiz-btn {
        font-family: inherit; font-size: 15px; font-weight: 600;
        padding: 12px 32px; border-radius: 12px; border: none;
        cursor: pointer; transition: all 0.2s;
      }
      .quiz-btn-primary {
        background: #2997FF; color: white;
      }
      .quiz-btn-primary:hover { background: #1a8aef; transform: scale(1.02); }
      .quiz-btn-primary:active { transform: scale(0.98); }

      @keyframes quizIn {
        from { opacity: 0; transform: translateY(12px) scale(0.97); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes quizFade {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }
}

export const quizEngine = new QuizEngine();
export default QuizEngine;
